/**
 * CL-PROPERTY-OS-002C3B — Production invite lifecycle controller used by ClientPortal.
 */

import { doc, getDoc } from "firebase/firestore";
import { PROPERTY_OS_CLIENT_OUTCOMES } from "./propertyOsCallableResultModel";
import { validatePortalInviteRecoveryEvidence } from "./portalInviteLifecycleValidation";
import {
  canSendVerificationEmailNow,
  markVerificationEmailSent,
  readVerificationResendCooldownAt,
  releaseVerificationSendLock,
  tryAcquireVerificationSendLock,
} from "./portalEmailVerificationSendGuard";
import { portalEmailVerificationSatisfiedFromUser } from "./portalEmailVerificationRolloutPolicy";

function safeStr(value) {
  return String(value ?? "").trim();
}

export function createAuthGenerationTracker() {
  let generation = 0;
  let boundUid = "";

  return {
    bindUid(uid = "") {
      const key = safeStr(uid);
      if (!key) return generation;
      if (key !== boundUid) {
        boundUid = key;
        generation += 1;
      }
      return generation;
    },
    getGeneration() {
      return generation;
    },
    reset() {
      boundUid = "";
      generation += 1;
    },
    isCurrent(expectedGeneration) {
      return expectedGeneration === generation;
    },
  };
}

export function beginInviteLifecycleAttempt(eventLogRef) {
  const eventLog = [];
  if (eventLogRef && typeof eventLogRef === "object" && "current" in eventLogRef) {
    eventLogRef.current = eventLog;
  }
  return eventLog;
}

export async function revalidateRecoveryBeforeLegacyWrites({
  db,
  authUser,
  inviteCode,
  inviteData = null,
}) {
  const code = safeStr(inviteCode);
  let data = inviteData;
  if (!data && db && code) {
    const snap = await getDoc(doc(db, "invites", code));
    if (!snap.exists()) {
      return { ok: false, reason: "invite_unavailable" };
    }
    data = { id: snap.id, ...snap.data() };
  }
  return validatePortalInviteRecoveryEvidence({
    authUser,
    inviteData: data,
    inviteCode: code,
  });
}

export async function sendPortalVerificationEmailGuarded({
  sendFn,
  nowMs = Date.now(),
} = {}) {
  if (!canSendVerificationEmailNow(nowMs)) {
    return { sent: false, reason: "cooldown" };
  }
  if (!tryAcquireVerificationSendLock()) {
    return { sent: false, reason: "in_flight" };
  }
  try {
    await sendFn();
    markVerificationEmailSent(nowMs);
    return { sent: true, sentAt: readVerificationResendCooldownAt() };
  } catch (error) {
    return { sent: false, error };
  } finally {
    releaseVerificationSendLock();
  }
}

export async function runSignInPropertyOsConversionForGeneration({
  authGenerationTracker,
  expectedGeneration,
  authUser,
  executeConversion,
  signInConversionGuard,
}) {
  const uid = safeStr(authUser?.uid);
  if (!uid || !authGenerationTracker?.isCurrent(expectedGeneration)) {
    return { ignored: true, reason: "stale_generation" };
  }

  try {
    await authUser.reload();
    const refreshedUid = safeStr(authUser?.uid);
    if (!authGenerationTracker.isCurrent(expectedGeneration) || refreshedUid !== uid) {
      signInConversionGuard?.releaseWithoutAttempt(uid);
      return { ignored: true, reason: "uid_changed" };
    }
    if (!portalEmailVerificationSatisfiedFromUser(authUser)) {
      signInConversionGuard?.releaseWithoutAttempt(uid);
      return { ignored: true, reason: "unverified" };
    }
    await authUser.getIdToken(true);
    if (!authGenerationTracker.isCurrent(expectedGeneration)) {
      signInConversionGuard?.releaseWithoutAttempt(uid);
      return { ignored: true, reason: "stale_generation" };
    }

    const result = await executeConversion();
    if (!authGenerationTracker.isCurrent(expectedGeneration) || safeStr(authUser?.uid) !== uid) {
      return { ignored: true, reason: "stale_generation" };
    }

    if (result?.outcome === PROPERTY_OS_CLIENT_OUTCOMES.RETRYABLE_FAILURE) {
      signInConversionGuard?.markRetryableFailure(uid);
      return {
        ok: false,
        retryable: true,
        clientMessage: result?.error?.clientMessage || "Property portal conversion needs another try.",
      };
    }
    if (result?.outcome === PROPERTY_OS_CLIENT_OUTCOMES.SUCCESS || result?.outcome === PROPERTY_OS_CLIENT_OUTCOMES.NOT_APPLICABLE) {
      signInConversionGuard?.markSucceeded(uid);
      return { ok: true };
    }
    signInConversionGuard?.markRetryableFailure(uid);
    return {
      ok: false,
      retryable: true,
      clientMessage: "Property portal conversion needs another try.",
    };
  } catch (error) {
    if (authGenerationTracker.isCurrent(expectedGeneration) && safeStr(authUser?.uid) === uid) {
      signInConversionGuard?.markRetryableFailure(uid);
    } else {
      signInConversionGuard?.releaseWithoutAttempt(uid);
    }
    return {
      ok: false,
      retryable: true,
      clientMessage: "Property portal conversion needs another try.",
      error,
    };
  }
}
